import * as yup from "yup";
import { CampaignType } from "../../../features/campaign/campaignTypes";
import { hasGiftLinkPlaceholder } from "../../../utils/giftLinkPlaceholder";
import { EmailTemplate, TemplateType } from "../../settings/interfaces";

export const getTemplateDeliveryType = (template: EmailTemplate): string | undefined => {
  const tt = template.template_type;
  if (
    tt === TemplateType.TEXTMESSAGE ||
    tt === TemplateType.EMAIL ||
    tt === TemplateType.TEXTMESSAGE_AND_EMAIL
  ) {
    return tt;
  }
  const alt = (template as any).type;
  if (
    alt === TemplateType.TEXTMESSAGE ||
    alt === TemplateType.EMAIL ||
    alt === TemplateType.TEXTMESSAGE_AND_EMAIL
  ) {
    return alt;
  }
  return undefined;
};

export const CAMPAIGN_TYPE_SELECT_OPTIONS = [
  {
    label: "Gift",
    value: CampaignType.GIFT,
    description: "Use when your campaign includes a gift or gift card. Requires a gift link.",
  },
  {
    label: "Mailer",
    value: CampaignType.NO_GIFT,
    description: "Use for message-only campaigns without a gift.",
  },
];

export const FREQUENCY_OPTIONS = [
  { label: "Once", value: "once" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half-Yearly", value: "half_yearly" },
  { label: "Yearly", value: "yearly" },
  { label: "Every 2 years", value: "every_2_years" },
  { label: "Every 3 years", value: "every_3_years" },
  { label: "Every 4 years", value: "every_4_years" },
  { label: "Every 5 years", value: "every_5_years" },
];

export const BIRTHDAY_OCCASION_ID = "b6558d9b-0792-4e0e-be1e-816ff88ec7bf";
const BIRTHDAY_DEFAULT_YEAR = 1900;

export const isBirthdayOccasion = (
  occasion?: { value?: string; label?: string; name?: string } | null,
): boolean => {
  const id = String(occasion?.value || "").toLowerCase();
  const name = String(occasion?.label || occasion?.name || "")
    .trim()
    .toLowerCase();
  return id === BIRTHDAY_OCCASION_ID || name === "birthday";
};

export const formatCampaignEventDate = (
  value: string,
  birthdayMode: boolean,
): string => {
  const digits = value.replace(/\D/g, "");
  if (birthdayMode) {
    const limited = digits.slice(0, 4);
    if (limited.length <= 2) return limited;
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}`;
  }
  const limited = digits.slice(0, 8);
  if (limited.length === 0) return "";
  if (limited.length <= 4) return limited;
  if (limited.length <= 6) return `${limited.slice(0, 4)}/${limited.slice(4)}`;
  return `${limited.slice(0, 4)}/${limited.slice(4, 6)}/${limited.slice(6, 8)}`;
};

export const toBirthdayEventDate = (value: string): string => {
  const parts = value.trim().split("/").map((part) => part.trim());
  if (parts.length === 3) {
    return `${parts[1].padStart(2, "0")}/${parts[2].padStart(2, "0")}`;
  }
  if (parts.length === 2) {
    return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}`;
  }
  return value.trim();
};

export const toFullEventDate = (value: string): string => {
  const trimmed = value.trim();
  if (/^\d{2}\/\d{2}$/.test(trimmed)) {
    return `${BIRTHDAY_DEFAULT_YEAR}/${trimmed}`;
  }
  return trimmed;
};

export const displaySlashDateToIso = (yyyymmdd: string): string => {
  const parts = yyyymmdd.trim().split("/").map((p) => p.trim());
  if (parts.length === 2) {
    const month = Number(parts[0]);
    const day = Number(parts[1]);
    if (!month || !day || month > 12 || day > 31) return "";
    return `${BIRTHDAY_DEFAULT_YEAR}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  if (parts.length !== 3) return "";
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day || month > 12 || day > 31) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const apiEventDateToDisplaySlash = (
  raw: unknown,
  birthdayMode = false,
): string => {
  const str = String(raw ?? "").trim();
  if (!str) return "";
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return birthdayMode || Number(m[1]) === BIRTHDAY_DEFAULT_YEAR
      ? `${m[2]}/${m[3]}`
      : `${m[1]}/${m[2]}/${m[3]}`;
  }
  const eventDate = raw instanceof Date ? raw : new Date(str);
  if (!isNaN(eventDate.getTime())) {
    const year = eventDate.getUTCFullYear();
    const month = String(eventDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(eventDate.getUTCDate()).padStart(2, "0");
    return birthdayMode || year === BIRTHDAY_DEFAULT_YEAR
      ? `${month}/${day}`
      : `${year}/${month}/${day}`;
  }
  return "";
};

export const getShippingAndHandling = (price: number): number => {
  return price <= 50 ? 18 : 15;
};

/** Normalize to (000) 000-0000 for campaign recipient mobile fields. */
export const formatCampaignMobile = (value: string | undefined | null): string => {
  const digits = String(value ?? "").replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits.slice(0, 10);
  if (normalized.length !== 10) return String(value ?? "").trim();
  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
};

const isValidCampaignMobile = (value: string | undefined | null): boolean => {
  if (!value || !String(value).trim()) return true;
  const digits = String(value).replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return normalized.length === 10;
};

const recipientSchema = yup
  .object({
    name: yup.string().required("Name is required"),
    email: yup
      .string()
      .transform((v) => (v == null ? "" : String(v).trim()))
      .test(
        "email-format",
        "Invalid email",
        (v) => !v || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v)
      ),
    mobile: yup
      .string()
      .transform((v) => formatCampaignMobile(v))
      .test(
        "mobile-format",
        "Invalid mobile format (use (000) 000-0000)",
        (v) => isValidCampaignMobile(v)
      ),
    eventDate: yup
      .string()
      .required("Event date is required")
      .when("$birthdayMode", {
        is: true,
        then: (schema) =>
          schema.matches(
            /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/,
            "Format: MM/DD",
          ),
        otherwise: (schema) =>
          schema.matches(
            /^\d{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/,
            "Format: YYYY/MM/DD",
          ),
      }),
    price: yup
      .object({
        label: yup.string(),
        value: yup.string(),
      })
      .when("$campaignType", {
        is: (campaignType: any) => campaignType?.value === CampaignType.GIFT,
        then: (schema) =>
          schema.test("price-required", "Price is required", function (value) {
            if (!value || !value.value) {
              return this.createError({ message: "Price is required" });
            }
            return true;
          }),
        otherwise: (schema) => schema.nullable().optional(),
      }),
  })
  .test("email-or-mobile", "Enter an email or phone number (or both)", function (recipient) {
    const email = (recipient?.email || "").trim();
    const mobile = (recipient?.mobile || "").trim();
    if (!email && !mobile) {
      return this.createError({
        path: "email",
        message: "Enter an email or phone number (or both)",
      });
    }
    return true;
  });

export const recipientsOnlySchema = yup.object({
  recipients: yup.array().of(recipientSchema),
});

export interface Recipient {
  name: string;
  email: string;
  mobile: string;
  eventDate: string;
  price: {
    label: string;
    value: string;
  };
}

export interface CampaignFormData {
  name: string;
  campaignType: {
    label: string;
    value: string;
  };
  frequency: {
    label: string;
    value: string;
  };
  giftType: {
    label: string;
    value: string;
  };
  budget: {
    label: string;
    value: string;
  };
  occasion: {
    label: string;
    value: string;
  };
  emailTemplate?: {
    label?: string;
    value?: string;
  };
  subject: string;
  body: string;
  smsContent?: string;
  handwrittenNote?: string;
  recipients: Recipient[];
}

export const step1Schema = yup.object({
  name: yup.string().required("Campaign name is required"),
  campaignType: yup
    .object({
      label: yup.string().required("Campaign type is required"),
      value: yup.string().required("Campaign type is required"),
    })
    .required("Campaign type is required"),
  frequency: yup
    .object({
      label: yup.string().required(),
      value: yup.string().required(),
    })
    .required("Frequency is required"),
  smsContent: yup.string().required("SMS content is required"),
  giftType: yup
    .object({
      label: yup.string(),
      value: yup.string(),
    })
    .when("campaignType", {
      is: (value: any) => value?.value === CampaignType.GIFT,
      then: (schema) =>
        schema
          .required("Gift type is required")
          .test("gift-type-required", "Gift type is required", function (value) {
            if (!value || !value.label || !value.value) {
              return this.createError({ message: "Gift type is required" });
            }
            return true;
          }),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  budget: yup
    .object({
      label: yup.string(),
      value: yup.string(),
    })
    .when("campaignType", {
      is: (value: any) => value?.value === CampaignType.GIFT,
      then: (schema) =>
        schema
          .required("Budget is required")
          .test("budget-required", "Budget is required", function (value) {
            if (!value || !value.label || !value.value) {
              return this.createError({ message: "Budget is required" });
            }
            return true;
          }),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  occasion: yup
    .object({
      label: yup.string().required("Occasion is required"),
      value: yup.string().required("Occasion is required"),
    })
    .required("Occasion is required"),
  emailTemplate: yup
    .object({
      label: yup.string().optional(),
      value: yup.string().optional(),
    })
    .when("campaignType", {
      is: (campaignType: any) => campaignType?.value === CampaignType.GIFT,
      then: (schema) =>
        schema
          .required("Email template is required")
          .test(
            "email-template-required",
            "Email template is required",
            (value) => Boolean(value?.value)
          ),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  subject: yup.string().required("Subject is required"),
  body: yup
    .string()
    .required("Email body is required")
    .when("campaignType", {
      is: (campaignType: any) => campaignType?.value === CampaignType.GIFT,
      then: (schema) =>
        schema.test(
          "must-contain-gift-link",
          'Body must include "{gift_link}"',
          (value) => hasGiftLinkPlaceholder(value || "")
        ),
      otherwise: (schema) => schema,
    }),
  recipients: yup.array().of(recipientSchema),
});

/** Flatten react-hook-form / yup error objects into user-facing messages. */
export const collectFormErrorMessages = (errors: unknown, limit = 3): string[] => {
  const messages: string[] = [];

  const walk = (node: unknown) => {
    if (!node || messages.length >= limit) return;
    if (typeof node !== "object") return;

    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    const record = node as Record<string, unknown>;
    if (typeof record.message === "string" && record.message.trim()) {
      messages.push(record.message.trim());
      return;
    }

    Object.values(record).forEach(walk);
  };

  walk(errors);
  return [...new Set(messages)].slice(0, limit);
};
